import { Component, ErrorInfo, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

interface Props extends WithTranslation {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log the error details
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // Check if this is an offline navigation error
        if (error.message.includes('Failed to fetch dynamically imported module')) {
            console.log('[ErrorBoundary] Detected offline navigation error - user is offline');
        }
    }

    render() {
        const { t } = this.props;

        if (this.state.hasError) {
            // Check if this is an offline navigation error
            const isOfflineError = this.state.error?.message?.includes('Failed to fetch dynamically imported module');

            return (
                <div className="flex justify-center items-center min-h-screen bg-background">
                    <div className="text-center p-8 max-w-md">
                        <div className="text-6xl mb-4">😵</div>
                        <h1 className="text-2xl font-bold text-foreground mb-2">
                            {isOfflineError ? t('errors.offlineNavigation.title', 'Offline Navigation Error') : t('errors.errorBoundary.title')}
                        </h1>
                        <p className="text-muted-foreground mb-6">
                            {isOfflineError
                                ? t('errors.offlineNavigation.description', 'You\'re currently offline and this page hasn\'t been cached yet. Please check your connection and try again.')
                                : t('errors.errorBoundary.description')
                            }
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2 px-4 rounded-lg transition-colors"
                        >
                            {t('errors.errorBoundary.refreshButton')}
                        </button>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="mt-4 text-left">
                                <summary className="cursor-pointer text-sm text-muted-foreground">
                                    {t('errors.errorBoundary.errorDetails')}
                                </summary>
                                <pre className="mt-2 text-xs text-destructive bg-destructive/10 p-2 rounded overflow-auto">
                                    {this.state.error.toString()}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default withTranslation()(ErrorBoundary);
